import { Box } from "@mui/material"
import type { FC } from "react"
import { useMemo } from "react"
import { useFormContext, useWatch } from "react-hook-form"

import { EMPTY_STRING_ARRAY, FREIGHT_TERM_OPTIONS } from "./constants"
import { EffectiveDateFields } from "./EffectiveDateFields"
import { LinkedAccountsSection } from "./LinkedAccountsSection"
import { PricingTierTable } from "./PricingTierTable"
import type { SpecialPricingFormValues } from "./schemas"
import { ShipFromSelector } from "./ShipFromSelector"

import { FormCurrencyInput, FormMultiSelect, FormNumericField, FormSelect } from "@/components/UiKit"
import type { Incoterms, IncotermsDefinitionMap, ProductCompanyLocationAssociation } from "@/graphql/codegen/graphql"
import { useMergedIncotermsLocations } from "@/hooks/useMergedIncotermsLocations"
import { useGetIncotermsQuery } from "@/screens/Procurement/PurchaseOrder/DeliveryTermsAndShippingLegs/hooks/deliveryTerms/useGetIncotermsQuery"
import { AddressFormat } from "@/types/common"
import type { Option } from "@/types/form-fields"
import { formatLocationAddress } from "@/utils/utils"

interface SpecialPriceFieldsProps {
  productId: number
  shipFromOptions: Option<string>[]
  productLocationsAssociations: ProductCompanyLocationAssociation[]
}

export const SpecialPriceFields: FC<SpecialPriceFieldsProps> = ({
  productId,
  shipFromOptions,
  productLocationsAssociations,
}) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<SpecialPricingFormValues>()
  const selectedShipFromIds = useWatch({ control, name: "shipFromLocationIds" }) ?? EMPTY_STRING_ARRAY

  const { data: incotermsData } = useGetIncotermsQuery()
  const { locations: incotermsLocations } = useMergedIncotermsLocations(productLocationsAssociations)

  const incotermsLocationOptions = useMemo<Option<string>[]>(
    () =>
      incotermsLocations.map((location) => ({
        label: formatLocationAddress(location, AddressFormat.LocationAndAddress),
        value: location.locationId,
      })),
    [incotermsLocations]
  )

  const incotermsOptions = useMemo<Option<Incoterms>[]>(
    () =>
      incotermsData
        .filter((incoterm): incoterm is IncotermsDefinitionMap & { key: Incoterms } => Boolean(incoterm.key))
        .map((incoterm) => ({ label: incoterm.key, value: incoterm.key })),
    [incotermsData]
  )

  const handleShipFromAdd = (warehouseId: string) => {
    setValue("shipFromLocationIds", [...selectedShipFromIds, warehouseId], {
      shouldDirty: true,
      shouldValidate: true,
    })
  }

  const handleShipFromRemove = (warehouseId: string) => {
    setValue(
      "shipFromLocationIds",
      selectedShipFromIds.filter((id) => id !== warehouseId),
      { shouldDirty: true, shouldValidate: true }
    )
  }

  return (
    <Box className='flex flex-col gap-3'>
      <FormNumericField
        control={control}
        name='estimatedAnnualVolume'
        placeholder='Estimated Annual Volume (optional)'
        data-testid={`sales.product-pricing-form.estimated-annual-volume-field-${productId}`}
      />
      <FormCurrencyInput
        control={control}
        name='pricePoint'
        placeholder='Price Point (optional)'
        data-testid={`sales.product-pricing-form.price-point-field-${productId}`}
      />
      <FormMultiSelect
        control={control}
        name='incoterms'
        placeholder='Incoterms'
        required
        options={incotermsOptions}
        data-testid={`sales.product-pricing-form.incoterms-field-${productId}`}
      />
      <FormSelect
        control={control}
        name='incotermsLocationId'
        placeholder='Incoterms Location'
        required
        options={incotermsLocationOptions}
        data-testid={`sales.product-pricing-form.incoterms-location-field-${productId}`}
      />
      <FormSelect
        control={control}
        name='freightTerms'
        placeholder='Freight Terms'
        required
        options={FREIGHT_TERM_OPTIONS}
        data-testid={`sales.product-pricing-form.freight-terms-field-${productId}`}
      />
      <ShipFromSelector
        mode='multi'
        productId={productId}
        shipFromOptions={shipFromOptions}
        selectedShipFromIds={selectedShipFromIds}
        error={errors.shipFromLocationIds}
        onAdd={handleShipFromAdd}
        onRemove={handleShipFromRemove}
      />

      <LinkedAccountsSection productId={productId} />
      <PricingTierTable productId={productId} />
      <EffectiveDateFields productId={productId} />
    </Box>
  )
}
